import type {
  PlanV2IssueDispositionRecord,
  PlanV2IssueType,
  PlanV2WorkItemsUrlGroup
} from './plan-v2-issues';
import { sortWorkItemsByUrlGroups, sortWorkItemsWithinGroup } from './plan-v2-work-item-display';

/**
 * Which work items belong to a PAGE, and which only happen to carry one.
 *
 * Every disposition row stores a `urlId`, and for most of them that is the
 * subject: the freshness task on /pricing is about /pricing. Two types store it
 * as provenance instead — the row is keyed by a check CODE, and the urlId
 * records nothing more than which page the operator was looking at when they
 * decided:
 *
 * - `site_wide_technical` — a template defect. One fix clears the finding on
 *   every page that has it. Filed under the page it was sent from, it reads as
 *   that page's job, and a writer handed the list would go and edit one page.
 * - `check_muted` — "not fixing this kind of finding", for the period. Never
 *   reaches a work-items list (it lives in the dismissed queue), but it is the
 *   same shape of row and re-opening a page must not silently take it back.
 *
 * So: site-scoped rows group under Site-wide, and a per-page re-open leaves
 * them alone. See `utils/plan-v2-url-reopen`.
 */
export function isSiteScopedIssueType(issueType: PlanV2IssueType): boolean {
  return issueType === 'site_wide_technical' || issueType === 'check_muted';
}

export function isSiteScopedWorkItem(
  item: Pick<PlanV2IssueDispositionRecord, 'issueType'>
): boolean {
  return isSiteScopedIssueType(item.issueType);
}

/** Stable id for the Site-wide group — no URL to key it by. */
export const SITE_WIDE_GROUP_ID = '__site_wide__';

export const SITE_WIDE_GROUP_LABEL = 'Site-wide';

export const SITE_WIDE_GROUP_NOTE =
  'Template fixes that clear the same finding on every page. Decided while reviewing one page, but the work belongs to the site.';

export interface PlanV2WorkItemGroup extends PlanV2WorkItemsUrlGroup {
  /** `site_wide` groups have no page behind them: `url` is empty. */
  kind: 'site_wide' | 'url';
  /** What the group header shows — the URL, or "Site-wide". */
  label: string;
}

/** Group by URL, as the work-items list has always done. */
export function groupWorkItemsByUrl(
  items: readonly PlanV2IssueDispositionRecord[] | null | undefined
): PlanV2WorkItemsUrlGroup[] {
  const byUrl = new Map<string, PlanV2WorkItemsUrlGroup>();
  for (const item of items ?? []) {
    const urlId = (item?.urlId || '').trim();
    const url = (item?.url || '').trim();
    if (!urlId || !url) continue;
    let group = byUrl.get(urlId);
    if (!group) {
      group = { urlId, url, priorityScore: item.priorityScore ?? 0, items: [] };
      byUrl.set(urlId, group);
    }
    group.items.push(item);
    group.priorityScore = Math.max(group.priorityScore, item.priorityScore ?? 0);
  }
  return sortWorkItemsByUrlGroups([...byUrl.values()]);
}

/**
 * The work-items list: Site-wide first, then a group per page.
 *
 * Site-wide sits ABOVE rather than below because it is the cheapest work on the
 * page — one template edit against a finding the crawler raised on hundreds of
 * URLs — and because burying it under forty-five page groups is how it stayed
 * invisible while filed under a page.
 *
 * Nothing is dropped. A site-wide row that was previously shown under whatever
 * page it was sent from is shown here instead, so the totals an operator counts
 * still add up.
 */
export function groupWorkItemsForDisplay(
  items: readonly PlanV2IssueDispositionRecord[] | null | undefined
): PlanV2WorkItemGroup[] {
  const siteWide: PlanV2IssueDispositionRecord[] = [];
  const perPage: PlanV2IssueDispositionRecord[] = [];
  for (const item of items ?? []) {
    if (!item) continue;
    if (isSiteScopedWorkItem(item)) siteWide.push(item);
    else perPage.push(item);
  }

  const groups: PlanV2WorkItemGroup[] = [];
  if (siteWide.length) {
    groups.push({
      kind: 'site_wide',
      label: SITE_WIDE_GROUP_LABEL,
      urlId: SITE_WIDE_GROUP_ID,
      url: '',
      priorityScore: siteWide.reduce((max, item) => Math.max(max, item.priorityScore ?? 0), 0),
      items: sortWorkItemsWithinGroup(siteWide)
    });
  }

  for (const group of groupWorkItemsByUrl(perPage)) {
    groups.push({ ...group, kind: 'url', label: group.url });
  }

  return groups;
}

/** The per-page groups only — what "N pages" counts, and what a re-open acts on. */
export function urlWorkItemGroups(groups: readonly PlanV2WorkItemGroup[]): PlanV2WorkItemsUrlGroup[] {
  return groups.filter((group) => group.kind === 'url');
}
